// usePendingSlot.ts — Hook for click-to-place pre-fill integration
// Reads pendingSlotTarget from the store on mount. If it matches the
// dialog's slot group, enables the target slot with pre-filled coordinates
// and a generated mark. Clears the pending target after applying.

import { useState, useEffect, type SetStateAction, type Dispatch } from 'react';
import { useStore } from '../store/useStore';
import { autoMark, type SlotGroupKey } from '../utils/slotUtils';

type Slot<T> = { enabled: boolean; data: T };

export function usePendingSlot<T extends object>(
  group: SlotGroupKey,
  baseSlots: Slot<T>[],
  prefill: (panelX: number, panelY: number, mark: string) => Partial<T>,
  defaultData: T,
): [Slot<T>[], Dispatch<SetStateAction<Slot<T>[]>>] {
  const [slots, setSlots] = useState<Slot<T>[]>(() => {
    const pending = useStore.getState().ui.pendingSlotTarget;
    if (!pending || pending.group !== group) return baseSlots;
    const mark = autoMark(group, baseSlots as Slot<{ mark: string }>[]);
    // Use defaultData for disabled slots (click-to-place always targets a disabled slot)
    // so the new entity gets sensible defaults (width, height, etc.) instead of all zeros.
    const slot = baseSlots[pending.index];
    const base = (slot && slot.enabled) ? slot.data : defaultData;
    const next = [...baseSlots];
    next[pending.index] = { enabled: true, data: { ...base, ...prefill(pending.panelX, pending.panelY, mark) } as T };
    return next;
  });

  useEffect(() => {
    const s = useStore.getState();
    if (s.ui.pendingSlotTarget?.group === group) {
      s.clearPendingSlotTarget();
    }
  }, [group]);

  return [slots, setSlots];
}
